import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Menu, Phone, MapPin, Mail, ChevronRight, MessageCircle, Instagram, Facebook, Calendar, Users, Accessibility, Armchair, Sparkles, CheckCircle, Car, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { toast } from 'sonner';
import ChatWidget from '@/components/ChatWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const OpportunitiesPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [startBookingOnOpen, setStartBookingOnOpen] = useState(false);

  // Function to open chat for booking
  const openChatForBooking = () => {
    setStartBookingOnOpen(true);
    setChatOpen(true);
  };

  // Reset startBookingOnOpen when chat closes
  const handleChatOpenChange = (open) => {
    setChatOpen(open);
    if (!open) {
      setStartBookingOnOpen(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'chair',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opportunities/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Thank you for your enquiry! We will be in touch soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: 'chair',
          experience: '',
          message: ''
        });
      } else {
        toast.error(data.message || 'Failed to submit enquiry. Please try again.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Users, title: "Busy & Friendly", description: "Join our thriving team in a welcoming, supportive environment" },
    { icon: CheckCircle, title: "Established Since 2012", description: "Be part of Southport's most trusted salon with proven success" },
    { icon: Armchair, title: "12 Sections Available", description: "Plenty of room to build your own client base" },
    { icon: Accessibility, title: "Fully Accessible", description: "Wheelchair friendly premises for all clients" },
    { icon: Car, title: "Disabled Parking", description: "Convenient parking available right outside" },
    { icon: MapPin, title: "Prime Location", description: "Town centre location trusted by local hotels" }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="opportunities-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Join Us | Chair Rental & Beauty Room | iSalon UK Southport</title>
        <meta name="description" content="Looking for a chair to rent or beauty room in Southport? iSalon UK offers flexible chair rental and beauty room opportunities in our premier salon. Contact us today!" />
        <meta name="keywords" content="chair rental southport, beauty room rental, hairdresser chair rental, salon space southport, self employed hairdresser" />
        <link rel="canonical" href="https://www.isalon.uk/opportunities" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center" data-testid="logo">
              <img src={LOGO_URL} alt="iSalon UK" className="h-10 md:h-12 w-auto" />
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Home
              </Link>
              <Link to="/about" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                About
              </Link>
              <Link to="/prices" className="nav-link font-sans text-sm font-medium text-white/80 hover:text-isalonuk-accent transition-colors">
                Price List
              </Link>
              <span className="nav-link font-sans text-sm font-medium text-isalonuk-accent">
                Join Us
              </span>
              <Button onClick={openChatForBooking} className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full px-6">
                Book Now
              </Button>
            </div>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2 rounded-lg text-white">
                  <Menu size={40} strokeWidth={2.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-black border-l border-white/10 p-0">
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-white/10">
                    <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto mb-4" />
                    <Button onClick={openChatForBooking} className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full py-5 text-lg">
                      <Calendar className="mr-2" size={20} />
                      Book Now
                    </Button>
                  </div>
                  <div className="flex-1 p-6 overflow-y-auto">
                    <nav className="flex flex-col gap-4">
                      <SheetClose asChild>
                        <Link to="/" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Home
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/about" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          About
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/prices" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Price List
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                      <div className="py-3 px-4 rounded-xl font-sans text-lg font-medium text-isalonuk-accent bg-isalonuk-accent/10">
                        Join Us
                      </div>
                      <SheetClose asChild>
                        <Link to="/#contact" className="flex items-center justify-between py-3 px-4 rounded-xl font-sans text-lg font-medium text-white hover:bg-white/5">
                          Contact Us
                          <ChevronRight size={20} className="text-isalonuk-accent" />
                        </Link>
                      </SheetClose>
                    </nav>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-6 md:px-12" data-testid="opportunities-hero">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-isalonuk-accent transition-colors mb-8">
            <ArrowLeft size={18} />
            <span className="font-sans text-sm">Back to Home</span>
          </Link>
          
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-isalonuk-accent font-sans text-sm md:text-base font-medium tracking-widest uppercase mb-4">
              Unique Opportunity
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              Join Our Team
            </h1>
            <p className="text-white/70 font-sans text-base md:text-lg leading-relaxed mb-8">
              Looking for a new home for your hairdressing or beauty business? iSalon has exciting opportunities for talented professionals to rent a chair or beauty room in our busy, friendly Southport salon.
            </p>
          </div>
        </div>
      </section>

      {/* Opportunities Cards */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Rent a Chair */}
            <Card className="border border-white/10 bg-black rounded-2xl overflow-hidden" data-testid="chair-rental-card">
              <div className="aspect-[16/9] relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/rdmn0hwe_rentachair.jpg"
                  alt="Hair Styling Chair"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-isalonuk-accent text-black font-sans font-semibold text-sm px-4 py-2 rounded-full">
                    Chair Rental
                  </span>
                </div>
              </div>
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-isalonuk-accent/20 rounded-full flex items-center justify-center">
                    <Armchair className="text-isalonuk-accent" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-white">Rent a Chair</h3>
                    <p className="text-white/60 font-sans text-sm">For Hairdressers & Stylists</p>
                  </div>
                </div>
                <p className="text-white/70 font-sans leading-relaxed mb-6">
                  Perfect for experienced hairdressers looking to run their own business within a supportive, established salon environment. Build your client base while benefiting from our prime location and excellent reputation.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3 text-white/70 font-sans text-sm">
                    <CheckCircle size={18} className="text-isalonuk-accent flex-shrink-0" />
                    Flexible rental terms available
                  </li>
                  <li className="flex items-center gap-3 text-white/70 font-sans text-sm">
                    <CheckCircle size={18} className="text-isalonuk-accent flex-shrink-0" />
                    Access to salon equipment & facilities
                  </li>
                  <li className="flex items-center gap-3 text-white/70 font-sans text-sm">
                    <CheckCircle size={18} className="text-isalonuk-accent flex-shrink-0" />
                    High footfall town centre location
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Beauty Room */}
            <Card className="border border-white/10 bg-black rounded-2xl overflow-hidden" data-testid="beauty-room-card">
              <div className="aspect-[16/9] relative">
                <img
                  src="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&q=80"
                  alt="Beauty Room"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-isalonuk-accent text-black font-sans font-semibold text-sm px-4 py-2 rounded-full">
                    Beauty Room
                  </span>
                </div>
              </div>
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-isalonuk-accent/20 rounded-full flex items-center justify-center">
                    <Sparkles className="text-isalonuk-accent" size={24} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-white">Beauty Room</h3>
                    <p className="text-white/60 font-sans text-sm">For Beauty Professionals</p>
                  </div>
                </div>
                <p className="text-white/70 font-sans leading-relaxed mb-6">
                  A private beauty room available for rent. Ideal for beauty therapists, aestheticians, or wellness practitioners looking for a professional space to grow their business.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3 text-white/70 font-sans text-sm">
                    <CheckCircle size={18} className="text-isalonuk-accent flex-shrink-0" />
                    Private treatment room
                  </li>
                  <li className="flex items-center gap-3 text-white/70 font-sans text-sm">
                    <CheckCircle size={18} className="text-isalonuk-accent flex-shrink-0" />
                    Professional, clean environment
                  </li>
                  <li className="flex items-center gap-3 text-white/70 font-sans text-sm">
                    <CheckCircle size={18} className="text-isalonuk-accent flex-shrink-0" />
                    Cross-referral opportunities
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Join iSalon & Enquiry Form - Reordered on mobile */}
      <div className="flex flex-col-reverse md:flex-col">
        {/* Why Join iSalon - Shows second on mobile (appears after form due to col-reverse) */}
        <section className="py-16 md:py-24 px-6 md:px-12 bg-black">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Why iSalon?</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-bold mb-4">
                The Perfect Place to Grow
              </h2>
              <p className="text-white/60 font-sans max-w-2xl mx-auto">
                Since 2012, we've built a reputation as one of Southport's busiest and most friendly salons. Here's why professionals choose iSalon:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border border-white/10 bg-zinc-900 rounded-2xl p-6" data-testid={`benefit-card-${index}`}>
                  <div className="w-12 h-12 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mb-4">
                    <benefit.icon className="text-isalonuk-accent" size={24} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-white/60 font-sans text-sm leading-relaxed">{benefit.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Enquiry Form - Shows first on mobile (due to col-reverse) */}
        <section className="py-16 md:py-24 px-6 md:px-12 bg-zinc-900" data-testid="enquiry-section">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-isalonuk-accent font-sans text-sm font-medium tracking-widest uppercase mb-3">Get Started</p>
              <h2 className="font-heading text-3xl md:text-4xl text-white font-bold mb-4">
                Enquire Today
              </h2>
              <p className="text-white/60 font-sans">
                Interested in joining our team? Fill out the form below and we'll be in touch to discuss your requirements.
              </p>
            </div>

          <Card className="border border-white/10 bg-black rounded-2xl p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="enquiry-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white font-sans">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    className="bg-zinc-900 border-white/20 text-white placeholder:text-white/40 focus:border-isalonuk-accent"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white font-sans">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="bg-zinc-900 border-white/20 text-white placeholder:text-white/40 focus:border-isalonuk-accent"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white font-sans">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="07xxx xxxxxx"
                    className="bg-zinc-900 border-white/20 text-white placeholder:text-white/40 focus:border-isalonuk-accent"
                    data-testid="input-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interest" className="text-white font-sans">I'm Interested In *</Label>
                  <select
                    id="interest"
                    name="interest"
                    required
                    value={formData.interest}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-white/20 text-white focus:border-isalonuk-accent focus:outline-none focus:ring-1 focus:ring-isalonuk-accent"
                    data-testid="input-interest"
                  >
                    <option value="chair">Rent a Chair</option>
                    <option value="beauty">Beauty Room</option>
                    <option value="both">Both Options</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience" className="text-white font-sans">Years of Experience</Label>
                <Input
                  id="experience"
                  name="experience"
                  type="text"
                  value={formData.experience}
                  onChange={handleInputChange}
                  placeholder="e.g., 5 years in hairdressing"
                  className="bg-zinc-900 border-white/20 text-white placeholder:text-white/40 focus:border-isalonuk-accent"
                  data-testid="input-experience"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-white font-sans">Tell Us About Yourself</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Share your experience, specialities, and what you're looking for..."
                  className="bg-zinc-900 border-white/20 text-white placeholder:text-white/40 focus:border-isalonuk-accent resize-none"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full py-6 text-lg disabled:opacity-50"
                data-testid="submit-enquiry-btn"
              >
                {isSubmitting ? 'Sending...' : 'Send Enquiry'}
              </Button>
            </form>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-white/60 font-sans text-sm mb-4">Prefer to chat directly?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:01704500872">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6">
                  <Phone className="mr-2" size={18} />
                  01704 500872
                </Button>
              </a>
              <a href="https://wa.me/441704500872" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6">
                  <MessageCircle className="mr-2" size={18} />
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src={LOGO_URL} alt="iSalon UK" className="h-10 w-auto" />
              </div>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-1">
                Established in 2012
              </p>
              <p className="text-white/60 font-sans text-sm leading-relaxed mb-4">
                Experience · Knowledge · Skill · Quality
              </p>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/226899317381783" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <Facebook size={18} className="text-white" />
                </a>
                <a href="https://www.instagram.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <Instagram size={18} className="text-white" />
                </a>
                <a href="https://twitter.com/isalonuk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-isalonuk-accent transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2">
                <Link to="/about" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">About Us</Link>
                <Link to="/#services" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Services</Link>
                <Link to="/opportunities" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Join Us</Link>
                <Link to="/#contact" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">Contact</Link>
              </nav>
            </div>

            <div>
              <h4 className="font-sans font-semibold text-white mb-4">Contact</h4>
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-isalonuk-accent mt-1 flex-shrink-0" />
                  <span className="text-white/60 font-sans text-sm">89B London St, Southport, PR9 0TX</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="tel:01704500872" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">01704 500872</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <a href="mailto:bookings@isalon.uk" className="text-white/60 font-sans text-sm hover:text-isalonuk-accent">bookings@isalon.uk</a>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-isalonuk-accent flex-shrink-0" />
                  <button onClick={openChatForBooking} className="text-white/60 font-sans text-sm hover:text-isalonuk-accent text-left">Request Appointment</button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="text-white/40 font-sans text-sm">© {new Date().getFullYear()} iSalon UK. All rights reserved.</p>
              <p className="text-white/40 font-sans text-xs leading-relaxed max-w-lg">
                iSalon UK is a trading name of Scissors Limited, a company registered in England and Wales with company registration number 11875206.
              </p>
              <div className="flex flex-col items-center gap-1">
                <p className="text-isalonuk-accent font-sans text-sm font-medium">
                  Website designed and built by <a href="https://www.barton.uk" target="_blank" rel="noopener noreferrer" className="hover:underline">www.barton.uk</a>
                </p>
                <Link to="/privacy-policy" className="text-white/40 font-sans text-xs hover:text-isalonuk-accent transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <ChatWidget 
        isOpen={chatOpen} 
        onOpenChange={handleChatOpenChange} 
        startBookingOnOpen={startBookingOnOpen}
      />
    </div>
  );
};

export default OpportunitiesPage;
